import { useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, Clock, Send, CheckCircle2, MessageCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { submitContact } from "@/lib/api";

const SERVICE_OPTIONS = [
  "STEM Tutoring",
  "MMI Coaching",
  "Academic & Admissions Coaching",
  "Essay Preparation",
  "Not sure yet",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    service_interest: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await submitContact(form);
      setSubmitted(true);
      toast.success("Message received. I'll reply within 24 hours.");
      setForm({ name: "", email: "", service_interest: "", subject: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 lg:pt-24 pb-10">
        <p className="overline text-terracotta">Let's talk</p>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02] text-ink mt-4 max-w-3xl">
          Get in Touch.
        </h1>
        <p className="mt-6 text-lg text-ink-muted max-w-2xl leading-relaxed">
          Have questions about STEM coaching or veterinary applications? Reach out today to start your academic
          transformation with professional guidance. I personally reply within 24 hours.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          {submitted ? (
            <div
              data-testid="contact-success"
              className="bg-forest text-paper-50 p-10 rounded-sm flex flex-col items-start"
            >
              <CheckCircle2 className="w-12 h-12 text-scholar mb-5" strokeWidth={1.6} />
              <h2 className="font-display font-bold text-3xl tracking-tight">Message received.</h2>
              <p className="mt-3 text-paper-200/90 max-w-md leading-relaxed">
                Thanks for reaching out. I read every message personally and usually reply within 24 hours.
                In the meantime, feel free to poke around the blog.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                data-testid="contact-send-another"
                className="mt-6 inline-flex items-center gap-2 bg-scholar text-ink px-5 py-3 text-sm font-semibold rounded-sm hover:bg-scholar-dark transition-colors"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="bg-paper-100 border border-paper-200 p-7 lg:p-10 rounded-sm space-y-6"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="name" className="text-ink font-semibold">Your name *</Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={update("name")}
                    data-testid="contact-input-name"
                    className="mt-2 bg-paper-50 border-paper-200 focus-visible:ring-forest focus-visible:ring-2"
                    placeholder="Alex Johnson"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-ink font-semibold">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    data-testid="contact-input-email"
                    className="mt-2 bg-paper-50 border-paper-200 focus-visible:ring-forest focus-visible:ring-2"
                    placeholder="you@email.com"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-ink font-semibold">Service interest</Label>
                  <Select value={form.service_interest} onValueChange={update("service_interest")}>
                    <SelectTrigger
                      data-testid="contact-select-service"
                      className="mt-2 bg-paper-50 border-paper-200 focus:ring-forest focus:ring-2"
                    >
                      <SelectValue placeholder="Choose a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`service-option-${s.toLowerCase().replace(/\s+/g, "-")}`}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="subject" className="text-ink font-semibold">Subject</Label>
                  <Input
                    id="subject"
                    value={form.subject}
                    onChange={update("subject")}
                    data-testid="contact-input-subject"
                    className="mt-2 bg-paper-50 border-paper-200 focus-visible:ring-forest focus-visible:ring-2"
                    placeholder="e.g. MCAT prep timing"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="message" className="text-ink font-semibold">Your message *</Label>
                <Textarea
                  id="message"
                  value={form.message}
                  onChange={update("message")}
                  data-testid="contact-input-message"
                  rows={6}
                  className="mt-2 bg-paper-50 border-paper-200 focus-visible:ring-forest focus-visible:ring-2 resize-none"
                  placeholder="Tell me about your goals, timeline, or where you're stuck. The more detail, the better."
                  required
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="inline-flex items-center gap-2 bg-forest text-paper-50 px-7 py-4 text-base font-semibold rounded-sm hover:bg-forest-700 hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? "Sending…" : <>Send message <Send className="w-4 h-4" /></>}
              </button>
              <p className="text-xs text-ink-muted">
                Your details are used only to reply to your message. No lists, no spam.
              </p>
            </form>
          )}
        </div>

        <aside className="lg:col-span-5 space-y-5">
          <div className="rounded-sm overflow-hidden border border-paper-200">
            <img
              src="https://static.wixstatic.com/media/407be3_1681785657cc4311840f28ca2d9b7854~mv2.jpg/v1/fill/w_685,h_856,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Kopia%20DSC_5805_JPG.jpg"
              alt="Dr. Ashley Delmar"
              className="w-full h-[300px] object-cover"
            />
          </div>
          <div className="bg-ink text-paper-50 p-8 rounded-sm">
            <p className="overline text-scholar">Availability & Access</p>
            <div className="mt-5 space-y-5">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 mt-0.5 text-scholar shrink-0" />
                <div>
                  <p className="text-sm text-paper-200/70">Email</p>
                  <a href="mailto:ashleydelmar04@gmail.com" className="font-semibold link-underline" data-testid="contact-email-link">
                    ashleydelmar04@gmail.com
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MessageCircle className="w-5 h-5 mt-0.5 text-scholar shrink-0" />
                <div>
                  <p className="text-sm text-paper-200/70">WhatsApp</p>
                  <a href="https://wa.me/48516736168" target="_blank" rel="noreferrer" className="font-semibold link-underline" data-testid="contact-whatsapp-link">
                    +48 516 736 168
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 mt-0.5 text-scholar shrink-0" />
                <div>
                  <p className="text-sm text-paper-200/70">Session hours</p>
                  <p className="font-semibold">Mon to Fri · 9am to 11pm (GMT+2)</p>
                  <p className="font-semibold">Sat · 10am to 10pm (GMT+2)</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 mt-0.5 text-scholar shrink-0" />
                <div>
                  <p className="text-sm text-paper-200/70">Location</p>
                  <p className="font-semibold">Online via Zoom</p>
                  <p className="text-sm text-paper-200/70">In-person (select areas via inquiry)</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-scholar text-ink p-8 rounded-sm">
            <p className="font-display font-bold text-xl sm:text-[22px] tracking-tight leading-snug [text-wrap:balance]">
              First-gen, neurodivergent, or non-traditional?
            </p>
            <p className="mt-3 text-sm leading-relaxed">
              Mention it in your message. I prioritize intro calls for students who didn't get a roadmap at home.
            </p>
          </div>
        </aside>
      </section>
    </div>
  );
}
